import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  X, 
  Smartphone, 
  ShieldCheck, 
  ArrowRight, 
  RotateCcw, 
  AlertCircle, 
  Loader2, 
  Sparkles, 
  CheckCircle2,
  Lock,
  Edit2
} from 'lucide-react';
import { RecaptchaVerifier, ConfirmationResult } from 'firebase/auth';
import { auth } from '@/src/lib/firebase';
import { useAuth } from '@/src/context/AuthContext';
import { useRouter } from '@/src/router/RouterContext';
import { SaremiLogo } from './common/SaremiLogo';
import { triggerHaptic } from '../utils/haptics';

interface AuthModalProps {
  onClose: () => void;
  initialMode?: 'signin' | 'signup' | 'reset';
}

declare global {
  interface Window {
    recaptchaVerifier?: RecaptchaVerifier | null;
  }
}

export const AuthModal: React.FC<AuthModalProps> = ({ onClose }) => {
  const { sendPhoneOtp, verifyPhoneOtp } = useAuth();
  const { navigate } = useRouter();

  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [phoneDigits, setPhoneDigits] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null);
  
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [resendCountdown, setResendCountdown] = useState(0);

  const otpInputRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);

  // Countdown timer for Resend OTP
  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setInterval(() => {
      setResendCountdown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCountdown]);

  // Clean up reCAPTCHA verifier on unmount
  useEffect(() => {
    return () => {
      try {
        if (window.recaptchaVerifier) {
          window.recaptchaVerifier.clear();
          window.recaptchaVerifier = null;
        }
      } catch {
        // Safe ignore
      }
    };
  }, []);

  const getCleanPhone = () => phoneDigits.replace(/\D/g, '').slice(-10);
  const formattedPhoneDisplay = () => {
    const d = getCleanPhone();
    if (d.length === 10) {
      return `+91 ${d.slice(0, 5)} ${d.slice(5)}`;
    }
    return `+91 ${d}`;
  };

  const maskedPhoneDisplay = () => {
    const d = getCleanPhone();
    if (d.length === 10) {
      return `+91 ${d.slice(0, 2)}••• ••${d.slice(-3)}`;
    }
    return `+91 ${d}`;
  };

  const initRecaptchaVerifier = () => {
    try {
      if (window.recaptchaVerifier) {
        return window.recaptchaVerifier;
      }
      const verifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
        size: 'invisible',
        callback: () => {
          // reCAPTCHA solved
        },
        'expired-callback': () => {
          setErrorMessage('reCAPTCHA expired. Please try sending OTP again.');
        }
      });
      window.recaptchaVerifier = verifier;
      return verifier;
    } catch (err: any) {
      console.warn('reCAPTCHA initialization note:', err);
      // Fallback
      return null;
    }
  };

  // STEP 1: SEND OTP TO MOBILE NUMBER
  const handleSendOtp = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setErrorMessage(null);

    const clean = getCleanPhone();
    if (clean.length < 10) {
      setErrorMessage('Please enter a valid 10-digit mobile number.');
      triggerHaptic('warning');
      return;
    }

    setIsLoading(true);
    triggerHaptic('medium');

    try {
      const verifier = initRecaptchaVerifier();

      const fullPhoneNumber = `+91${clean}`;
      const confirmation = await sendPhoneOtp(fullPhoneNumber, verifier);
      
      setConfirmationResult(confirmation);
      setStep('otp');
      setResendCountdown(30);
      setIsLoading(false);
      triggerHaptic('success');

      // If dev OTP provided (e.g. testing or fallback mode), auto-fill or focus
      if (confirmation?.devOtp) {
        setOtpCode(confirmation.devOtp);
      }

      setTimeout(() => {
        otpInputRef.current?.focus();
      }, 200);
    } catch (err: any) {
      console.error('Phone OTP dispatch error:', err);
      setIsLoading(false);
      triggerHaptic('warning');

      let userFriendlyMsg = 'Failed to send OTP. Please check your mobile number.';
      if (err.code === 'auth/invalid-phone-number') {
        userFriendlyMsg = 'Invalid mobile number format. Please enter a valid 10-digit Indian number.';
      } else if (err.code === 'auth/too-many-requests') {
        userFriendlyMsg = 'Too many attempts. Please wait a few minutes before requesting another OTP.';
      } else if (err.code === 'auth/quota-exceeded') {
        userFriendlyMsg = 'SMS quota reached. Please contact academy support.';
      } else if (err.message) {
        userFriendlyMsg = err.message;
      }
      setErrorMessage(userFriendlyMsg);

      // Reset recaptcha verifier on error so it can be re-rendered
      try {
        if (window.recaptchaVerifier) {
          window.recaptchaVerifier.clear();
          window.recaptchaVerifier = null;
        }
      } catch {}
    }
  };

  // STEP 2: VERIFY OTP CODE
  const handleVerifyOtp = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setErrorMessage(null);

    const cleanOtp = otpCode.replace(/\D/g, '').trim();
    if (cleanOtp.length < 6) {
      setErrorMessage('Please enter the complete 6-digit OTP code.');
      triggerHaptic('warning');
      return;
    }

    if (!confirmationResult) {
      setErrorMessage('Session expired. Please request a new OTP.');
      setStep('phone');
      return;
    }

    setIsLoading(true);
    triggerHaptic('medium');

    try {
      await verifyPhoneOtp(confirmationResult, cleanOtp);
      triggerHaptic('heavy');
      setIsLoading(false);
      onClose();
      // Directly navigate to student application
      navigate('/app');
    } catch (err: any) {
      console.error('OTP Verification error:', err);
      setIsLoading(false);
      triggerHaptic('warning');

      let userFriendlyMsg = 'Incorrect OTP code. Please check and try again.';
      if (err.code === 'auth/invalid-verification-code') {
        userFriendlyMsg = 'Invalid OTP code. Please enter the 6-digit code received on your mobile.';
      } else if (err.code === 'auth/code-expired') {
        userFriendlyMsg = 'This OTP has expired. Please tap "Resend OTP" to receive a fresh code.';
      } else if (err.message) {
        userFriendlyMsg = err.message;
      }
      setErrorMessage(userFriendlyMsg);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      {/* Invisible container for Firebase reCAPTCHA */}
      <div id="recaptcha-container" />

      <motion.div 
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 8 }}
        className="bg-white rounded-3xl max-w-sm sm:max-w-md w-full p-6 sm:p-8 relative text-left shadow-2xl border border-amber-100/60 my-6"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 flex items-center justify-center font-bold transition-colors cursor-pointer"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Brand Header */}
        <div className="mb-5">
          <SaremiLogo size="md" className="h-9 sm:h-10 mb-1" alt="Saremi Academy" />
        </div>

        {/* Error Notification */}
        {errorMessage && (
          <div className="mb-4 p-3 rounded-2xl bg-rose-50 text-rose-800 text-xs flex items-start gap-2.5 border border-rose-200">
            <AlertCircle className="w-4 h-4 shrink-0 text-rose-600 mt-0.5" />
            <span className="leading-relaxed font-medium">{errorMessage}</span>
          </div>
        )}

        {/* ========================================================================= */}
        {/* SCREEN 1: ENTER MOBILE NUMBER */}
        {/* ========================================================================= */}
        {step === 'phone' && (
          <div className="space-y-5">
            <div>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-amber-50 text-[#8C6428] text-[10px] font-bold uppercase tracking-wider mb-2 border border-amber-200/60">
                <Sparkles className="w-3 h-3 text-[#D8A84A]" />
                Student Login
              </div>
              <h2 className="font-serif text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
                Welcome to SaReMi Academy
              </h2>
              <p className="text-xs sm:text-sm text-gray-500 font-medium mt-1">
                Enter your mobile number to access your classes, curriculum roadmap, and practice studio.
              </p>
            </div>

            <form onSubmit={handleSendOtp} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">
                  Mobile Number
                </label>
                <div className="flex items-center rounded-2xl border-2 border-gray-200 focus-within:border-gray-900 overflow-hidden bg-gray-50/50 transition-colors">
                  <div className="flex items-center gap-1 px-3.5 py-3 bg-gray-100/80 border-r border-gray-200 text-xs font-bold text-gray-800 shrink-0 select-none">
                    <span className="text-base leading-none">🇮🇳</span>
                    <span>+91</span>
                  </div>
                  <input
                    ref={phoneInputRef}
                    type="tel"
                    inputMode="numeric"
                    autoFocus
                    required
                    value={phoneDigits}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setPhoneDigits(digits);
                    }}
                    placeholder="98765 43210"
                    className="w-full px-3.5 py-3 text-sm font-bold text-gray-900 bg-transparent focus:outline-none placeholder:text-gray-400 placeholder:font-normal tracking-wider"
                  />
                </div>
                <p className="text-[11px] text-gray-400 font-medium mt-1.5 pl-1">
                  We will send a 6-digit OTP to verify your account.
                </p>
              </div>

              <button
                type="submit"
                disabled={isLoading || getCleanPhone().length < 10}
                className="w-full py-3.5 px-6 rounded-2xl bg-[#0F0F0F] hover:bg-black text-white font-extrabold text-xs uppercase tracking-wider flex items-center justify-center gap-2 shadow-md transition-all disabled:opacity-50 cursor-pointer active:scale-98"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin text-amber-400" />
                    <span>Sending OTP...</span>
                  </>
                ) : (
                  <>
                    <span>Continue</span>
                    <ArrowRight className="w-4 h-4 text-amber-400" />
                  </>
                )}
              </button>
            </form>

            {/* Faculty & Admin Portal discreet access */}
            <div className="pt-3 border-t border-gray-100 text-center">
              <span className="text-[11px] text-gray-400">Faculty or Admin Portal? </span>
              <button
                type="button"
                onClick={() => {
                  onClose();
                  navigate('/teacher-app');
                }}
                className="text-[11px] font-bold text-gray-700 hover:text-black hover:underline cursor-pointer"
              >
                Sign In here &rarr;
              </button>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* SCREEN 2: ENTER OTP */}
        {/* ========================================================================= */}
        {step === 'otp' && (
          <div className="space-y-5">
            <div>
              <button
                type="button"
                onClick={() => {
                  setStep('phone');
                  setErrorMessage(null);
                }}
                className="inline-flex items-center gap-1 text-[11px] font-bold text-gray-500 hover:text-gray-900 mb-2 transition-colors cursor-pointer"
              >
                <ArrowRight className="w-3.5 h-3.5 rotate-180" />
                <span>Change Mobile Number</span>
              </button>
              <h2 className="font-serif text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
                Enter OTP
              </h2>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="text-xs text-gray-500 font-medium">
                  OTP sent to <strong className="text-gray-900">{maskedPhoneDisplay()}</strong>
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setStep('phone');
                    setErrorMessage(null);
                  }}
                  className="text-[10px] font-bold text-[#8C6428] hover:underline cursor-pointer flex items-center gap-0.5"
                >
                  <Edit2 className="w-2.5 h-2.5" />
                  <span>Edit</span>
                </button>
              </div>
            </div>

            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <div>
                <input
                  ref={otpInputRef}
                  type="tel"
                  inputMode="numeric"
                  autoFocus
                  required
                  maxLength={6}
                  value={otpCode}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setOtpCode(digits);
                    if (digits.length === 6) {
                      // Trigger automatic submit when 6 digits filled
                      setTimeout(() => {
                        const submitBtn = document.getElementById('btn-verify-otp');
                        submitBtn?.click();
                      }, 100);
                    }
                  }}
                  placeholder="• • • • • •"
                  className="w-full py-3.5 px-4 text-center text-2xl font-mono font-bold tracking-[0.5em] rounded-2xl border-2 border-gray-200 focus:border-gray-900 focus:outline-none bg-gray-50/50 text-gray-900 placeholder:tracking-widest"
                />
                {confirmationResult?.devOtp && (
                  <div
                    onClick={() => {
                      setOtpCode(confirmationResult.devOtp!);
                      triggerHaptic('light');
                    }}
                    className="mt-2 py-1.5 px-3 rounded-xl bg-amber-50/90 border border-amber-200/80 text-[#8C6428] text-center text-xs font-semibold cursor-pointer hover:bg-amber-100 transition-all flex items-center justify-center gap-1.5"
                  >
                    <Sparkles className="w-3 h-3 text-[#D8A84A]" />
                    <span>Demo OTP: </span>
                    <strong className="font-mono tracking-widest text-[#0F0F0F] bg-white/80 px-1.5 py-0.5 rounded border border-amber-200">{confirmationResult.devOtp}</strong>
                    <span className="text-[10px] text-gray-500">(Tap to auto-fill)</span>
                  </div>
                )}
              </div>

              <button
                id="btn-verify-otp"
                type="submit"
                disabled={isLoading || otpCode.replace(/\D/g, '').length < 6}
                className="w-full py-3.5 px-6 rounded-2xl bg-[#0F0F0F] hover:bg-black text-white font-extrabold text-xs uppercase tracking-wider flex items-center justify-center gap-2 shadow-md transition-all disabled:opacity-50 cursor-pointer active:scale-98"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin text-amber-400" />
                    <span>Verifying OTP...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    <span>Verify & Continue</span>
                  </>
                )}
              </button>

              <div className="flex items-center justify-between text-xs pt-1 px-1">
                <span className="text-gray-400">Didn't receive the OTP?</span>
                {resendCountdown > 0 ? (
                  <span className="font-mono text-gray-400 text-[11px]">
                    Resend in {resendCountdown}s
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleSendOtp()}
                    className="font-bold text-[#8C6428] hover:text-black hover:underline cursor-pointer flex items-center gap-1 text-[11px]"
                  >
                    <RotateCcw className="w-3 h-3" />
                    <span>Resend OTP</span>
                  </button>
                )}
              </div>
            </form>
          </div>
        )}
      </motion.div>
    </div>
  );
};
